import React, { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../services/api';
import { getKeycloak, hasKeycloakConfig, initializeKeycloak } from '../services/keycloak';
 
const AuthContext = createContext();
 
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
 
const mapUserFromToken = (tokenParsed = {}) => {
  const email = tokenParsed.email || '';
  const name =
    tokenParsed.name ||
    tokenParsed.preferred_username ||
    (email ? email.split('@')[0] : 'User');
 
  return {
    id: tokenParsed.sub || tokenParsed.sid || Date.now(),
    name,
    email,
    career_goal: '',
    onboarding_completed: false,
  };
};
 
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [keycloakReady, setKeycloakReady] = useState(false);
 
  useEffect(() => {
    let refreshTimer = null;
    let isCancelled = false;
 
    const init = async () => {

      if (process.env.REACT_APP_DEV_MODE === 'true') {
        const devUser = {
          id: 1,
          name: 'Dev User',
          email: 'dev@aivion.ai',
          career_goal: 'AI/ML Engineer',
          onboarding_completed: true,
        };
        localStorage.setItem('token', 'dev-token');
        localStorage.setItem('user', JSON.stringify(devUser));
        setUser(devUser);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      if (!hasKeycloakConfig()) {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
          setIsLoading(false);
          setIsAuthenticated(false);
          setUser(null);
          return;
        }

        try {
          const backendUser = await authAPI.getCurrentUser();
          if (isCancelled) return;
          setUser(backendUser);
          localStorage.setItem('user', JSON.stringify(backendUser));
          setIsAuthenticated(true);
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
        } finally {
          if (!isCancelled) {
            setIsLoading(false);
          }
        }
        return;
      }
 
      const keycloak = getKeycloak();
      if (!keycloak) {
        setIsLoading(false);
        setIsAuthenticated(false);
        setUser(null);
        return;
      }
 
      try {
        const authenticated = await initializeKeycloak({
          onLoad: 'check-sso',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        });
 
        if (isCancelled) return;
        setKeycloakReady(true);
 
        if (!authenticated) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
 
        localStorage.setItem('token', keycloak.token || '');
        const tokenUser = mapUserFromToken(keycloak.tokenParsed);
        localStorage.setItem('user', JSON.stringify(tokenUser));
 
        try {
          const backendUser = await authAPI.getCurrentUser();
          const mergedUser = {
            ...tokenUser,
            ...backendUser,
            name: backendUser?.name || tokenUser.name,
            email: backendUser?.email || tokenUser.email,
          };
          setUser(mergedUser);
          localStorage.setItem('user', JSON.stringify(mergedUser));
        } catch {
          setUser(tokenUser);
        }
 
        setIsAuthenticated(true);
        setIsLoading(false);
 
        refreshTimer = setInterval(async () => {
          try {
            const refreshed = await keycloak.updateToken(60);
            if (refreshed && keycloak.token) {
              localStorage.setItem('token', keycloak.token);
            }
          } catch {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        }, 30000);
      } catch (error) {
        if (isCancelled) return;
        console.error('Keycloak initialization failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    };
 
    init();
 
    return () => {
      isCancelled = true;
      if (refreshTimer) clearInterval(refreshTimer);
    };
  }, []);
 
  const login = async () => {
    const keycloak = getKeycloak();
    if (!keycloak) {
      throw new Error('Keycloak is not configured correctly');
    }
    await keycloak.login({
      redirectUri: `${window.location.origin}/dashboard`,
    });
  };
 
  const signup = async () => {
    const keycloak = getKeycloak();
    if (!keycloak) {
      throw new Error('Keycloak is not configured correctly');
    }
    await keycloak.login({
      action: 'register',
      redirectUri: `${window.location.origin}/onboarding`,
    });
  };
 
  const logout = async () => {
    const keycloak = getKeycloak();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRoadmap');
    setUser(null);
    setIsAuthenticated(false);
    if (keycloak && keycloakReady) {
      await keycloak.logout({
        redirectUri: `${window.location.origin}/`,
      });
    }
  };

  const updateUser = (updates) => {
    setUser((prevUser) => {
      const nextUser = { ...(prevUser || {}), ...(updates || {}) };
      localStorage.setItem('user', JSON.stringify(nextUser));
      return nextUser;
    });
  };

  const refreshUser = async () => {
    const backendUser = await authAPI.getCurrentUser();
    setUser((prevUser) => {
      const nextUser = {
        ...(prevUser || {}),
        ...backendUser,
        name: backendUser?.name || prevUser?.name || 'User',
        email: backendUser?.email || prevUser?.email || '',
      };
      localStorage.setItem('user', JSON.stringify(nextUser));
      return nextUser;
    });
    return backendUser;
  };
 
  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    signup,
    logout,
    updateUser,
    refreshUser,
  };
 
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
 
export default AuthContext;
 
 
